import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Button from "components/shared/Button";
import Form, { Field } from "components/shared/Form";
import DatePicker from "components/shared/inputs/DatePicker";
import ResourceSelect from "components/shared/inputs/ResourceSelect";
import { useProfile } from "../../../../../hooks/useProfile";
import { useChangeUserInformationMutation } from "../../../../../api/useChangeUserInformationMutation";
import { useIsFieldDisabled } from "../../../../../hooks/useIsFieldDisabled";
import { useCompanyTimeZone } from "utils/hooks/company";
import { getSettingsResourceSearchFilters, renderSettingsResourceLabel } from "utils/helpers/settings";
import { applyTimezone, dateTimeParseZone } from "utils/helpers/date";
import type { TFunction } from "i18next";

type FieldDisabledMap = {
    date: boolean;
    hiringDate: boolean;
    employeeType: boolean;
    payGroup: boolean;
};

const resourceItemSchema = z.object({ id: z.union([z.string(), z.number()]) });

const createHiringInformationSchema = (t: TFunction, disabled: FieldDisabledMap) =>
    z.object({
        date: z.date().optional().refine((value) => disabled.date || !!value, {
            message: t("required-effective-date"),
        }),
        hiringDate: z.date().optional().refine((value) => disabled.hiringDate || !!value, {
            message: t("required-hiring-date"),
        }),
        employeeType: resourceItemSchema.nullable().refine((value) => disabled.employeeType || !!value, {
            message: t("required-employee-type"),
        }),
        payGroup: resourceItemSchema.nullable().refine((value) => disabled.payGroup || !!value, {
            message: t("required-pay-group"),
        }),
        jobGroup: resourceItemSchema.nullable().optional(),
        userGroups: z.array(resourceItemSchema).optional(),
        union: resourceItemSchema.nullable().optional(),
        project: resourceItemSchema.nullable().optional(),
    });

function HiringInformation({ close }: { close: () => void }) {
    const { t } = useTranslation();
    const { user } = useProfile();
    const { changeHiringInformation } = useChangeUserInformationMutation();
    const timezone = useCompanyTimeZone();
    const getIsFieldDisabled = useIsFieldDisabled("peopleApiConnection.hiringInformation");

    const defaultValues = useMemo(() => ({
        date: undefined as Date | undefined,
        hiringDate: user?.hiringDate ? dateTimeParseZone(user?.hiringDate, "date") : undefined as Date | undefined,
        jobGroup: user?.jobGroup || null,
        payGroup: user?.payGroup || null,
        employeeType: user?.employeeType || null,
        userGroups: user?.userGroups || [],
        union: user?.union || null,
        project: user?.project || null,
    }), [user]);

    const schema = useMemo(() => createHiringInformationSchema(t, {
        date: getIsFieldDisabled("date"),
        hiringDate: getIsFieldDisabled("hiringDate"),
        employeeType: getIsFieldDisabled("employeeType"),
        payGroup: getIsFieldDisabled("payGroup"),
    }), [t, getIsFieldDisabled]);

    const form = useForm({ defaultValues, resolver: zodResolver(schema) });

    const onFinish = (value: typeof defaultValues) => {
        const data = {
            jobGroup: value.jobGroup?.id,
            payGroup: value.payGroup?.id,
            employeeType: value.employeeType?.id,
            userGroups: value.userGroups?.map(({ id }: { id: any }) => id) || [],
            union: value.union?.id,
            project: value.project?.id,
            hiringDate: applyTimezone(value.hiringDate, timezone, true),
            date: applyTimezone(value.date, timezone, true),
        };

        changeHiringInformation.mutate(data, {
            onSuccess: () => {
                toast.success(t("changes-saved-successfully"));
                close();
            },
        });
    };

    return (
        <Form form={form} onFinish={onFinish} className="tw-flex tw-flex-col tw-h-full">
            <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-flex tw-flex-col tw-gap-4 tw-pb-4">
                <Field label={t("effective-date")} name="date">
                    <DatePicker
                        placeholder={t("effective-date")}
                        disabled={getIsFieldDisabled("date")}
                    />
                </Field>

                <Field label={t("hiring-date")} name="hiringDate">
                    <DatePicker
                        placeholder={t("hiring-date")}
                        disabled={getIsFieldDisabled("hiringDate")}
                    />
                </Field>

                <Field label={t("employee-type")} name="employeeType">
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/employee-type"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        placeholder={t("employee-type")}
                        disabled={getIsFieldDisabled("employeeType")}
                    />
                </Field>

                <Field label={t("pay-group")} name="payGroup">
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/pay-group"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        placeholder={t("pay-group")}
                        disabled={getIsFieldDisabled("payGroup")}
                    />
                </Field>

                <Field label={t("job-group")} name="jobGroup">
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/job-groups"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        placeholder={t("job-group")}
                        disabled={getIsFieldDisabled("jobGroup")}
                    />
                </Field>

                <Field label={t("user-groups")} name="userGroups">
                    <ResourceSelect
                        resourcePath="/user-group"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        placeholder={t("user-groups")}
                        mode="multiple"
                        disabled={getIsFieldDisabled("userGroups")}
                    />
                </Field>

                <Field label={t("union")} name="union">
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/union"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        placeholder={t("union")}
                        disabled={getIsFieldDisabled("union")}
                    />
                </Field>

                <Field label={t("project")} name="project">
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/project"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        placeholder={t("project")}
                        disabled={getIsFieldDisabled("project")}
                    />
                </Field>
            </div>

            <div className="tw-shrink-0 tw-flex tw-justify-end tw-gap-2 tw-pt-4 tw-border-t">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={close}
                    disabled={changeHiringInformation.isPending}
                >
                    {t("cancel")}
                </Button>
                <Button
                    type="submit"
                    size="sm"
                    disabled={changeHiringInformation.isPending}
                >
                    {t("save")}
                </Button>
            </div>
        </Form>
    );
}

export default HiringInformation;
